import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  TextField, 
  Button, 
  Divider,
  Paper,
  InputAdornment,
  Stack
} from '@mui/material';
// ICON IMPORTS
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import MessageIcon from '@mui/icons-material/Message';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SendIcon from '@mui/icons-material/Send';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PublicIcon from '@mui/icons-material/Public';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle submission logic here (e.g., API call or EmailJS)
    console.log('Form Submitted:', formData);
    alert('Thank you for your inquiry. Our private client team will contact you shortly.');
    setFormData({ name: '', email: '', message: '' });
  };

  return (
    <Box sx={{ backgroundColor: '#050505', minHeight: '100vh', overflowX: 'hidden' }}>
      
      {/* ============ HERO SECTION ============ */}
      <Box
        sx={{
          position: 'relative',
          height: '70vh',
          minHeight: '550px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          overflow: 'hidden',
          backgroundImage: 'url(/images/hero.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        {/* Dark Overlay */}
        <Box sx={{ 
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
          background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.75) 50%, rgba(0, 0, 0, 0.95) 100%)', 
          zIndex: 1 
        }} />
        
        {/* Slow Zoom */}
        <Box sx={{ 
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
          backgroundImage: 'url(/images/hero.png)', backgroundSize: 'cover', backgroundPosition: 'center', 
          animation: 'slowZoom 25s ease-in-out infinite alternate', 
          '@keyframes slowZoom': { '0%': { transform: 'scale(1)' }, '100%': { transform: 'scale(1.1)' } }, 
          zIndex: 0 
        }} />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 10, pt: { xs: '140px', md: '180px' } }}>
          <Typography variant="overline" sx={{ color: '#d4af37', letterSpacing: '6px', fontWeight: 600, display: 'block', mb: 3, textTransform: 'uppercase', textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}>
            Private Client Services
          </Typography>
          
          <Typography variant="h1" component="h1" sx={{
            fontFamily: "'Playfair Display', serif", fontWeight: 600, fontSize: { xs: '3rem', sm: '3.5rem', md: '5rem' }, lineHeight: 1.1, mb: 4,
            background: 'linear-gradient(90deg, #ffffff 0%, #d4af37 50%, #ffffff 100%)', backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', animation: 'shimmer 6s linear infinite',
            filter: 'drop-shadow(0 4px 15px rgba(0,0,0,0.8))',
            '@keyframes shimmer': { '0%': { backgroundPosition: '200% center' }, '100%': { backgroundPosition: '-200% center' } },
          }}>
            Get In Touch
          </Typography>

          <Typography variant="body1" sx={{ color: '#e0e0e0', fontSize: { xs: '1rem', md: '1.2rem' }, fontWeight: 300, maxWidth: '700px', mx: 'auto', mb: 5, lineHeight: 1.8, textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}>
            Ready to make your next investment? Our specialists are available for private consultations and bespoke requests.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button component={RouterLink} to="/about" variant="outlined" endIcon={<ArrowForwardIcon />} sx={{
              borderColor: '#d4af37', color: '#d4af37', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '2px', textTransform: 'uppercase', padding: '18px 40px', borderRadius: '4px',
              '&:hover': { borderColor: '#ffffff', color: '#ffffff', backgroundColor: 'rgba(212, 175, 55, 0.1)' },
            }}>
              About Our Philosophy
            </Button>
          </Stack>
        </Container>
      </Box>
      {/* ============ END HERO ============ */}

      {/* ============ MAIN CONTENT ============ */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        
        {/* Split Layout */}
        <Grid container spacing={{ xs: 6, md: 8 }}>
          
          {/* Left Side: Premium Concierge Info */}
          <Grid item xs={12} md={5}>
            <Box sx={{ 
              p: 4, 
              background: 'linear-gradient(135deg, #141414 0%, #0a0a0a 100%)',
              border: '1px solid rgba(165, 164, 164, 0.2)',
              borderRadius: '6px',
              position: 'relative',
              overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '50%',
                background: 'linear-gradient(to bottom, rgba(255,255,255,0.05), transparent)',
                pointerEvents: 'none',
              }
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" sx={{ fontFamily: "'Playfair Display', serif", color: '#fff', fontSize: '1.8rem' }}>
                  Contact Details
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ 
                    minWidth: '45px', height: '45px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: '4px', border: '1px solid rgba(165, 164, 164, 0.3)',
                    backgroundColor: 'rgba(165, 164, 164, 0.05)', color: '#a5a4a4'
                  }}>
                    <LocationOnIcon sx={{ fontSize: '1.5rem' }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#a1a1a1', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '0.75rem', mb: 0.5 }}>
                      Corporate Headquarters
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <PublicIcon sx={{ color: '#d4af37', fontSize: '16px' }} />
                      <Typography variant="caption" sx={{ color: '#d4af37', fontWeight: 700, fontSize: '0.6rem', letterSpacing: '1px' }}>
                        US
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" sx={{ color: '#a1a1a1', fontSize: '0.85rem', lineHeight: 1.6 }}>
                      1100 NW Northwood Road
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#a1a1a1', fontSize: '0.85rem', lineHeight: 1.6 }}>
                      Seattle, Washington State, 98177
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ 
                    minWidth: '45px', height: '45px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: '4px', border: '1px solid rgba(165, 164, 164, 0.3)',
                    backgroundColor: 'rgba(165, 164, 164, 0.05)', color: '#a5a4a4'
                  }}>
                    <AccessTimeIcon sx={{ fontSize: '1.5rem' }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#a1a1a1', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '0.75rem', mb: 0.5 }}>
                      By Appointment Only
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 500 }}>
                      Mon - Sat: 10:00 AM - 6:00 PM
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ borderColor: 'rgba(165, 164, 164, 0.2)', my: 5 }} />

              <Typography variant="body2" sx={{ color: '#a1a1a1', lineHeight: 1.8 }}>
                *For immediate assistance regarding active orders or high-value transactions, please include your client reference number in your message.
              </Typography>
            </Box>
          </Grid>

          {/* Right Side: Premium Form */}
          <Grid item xs={12} md={7}>
            <Paper
              elevation={0}
              component="form"
              onSubmit={handleSubmit}
              sx={{
                p: { xs: 4, md: 6 },
                background: 'linear-gradient(135deg, #141414 0%, #0a0a0a 100%)',
                border: '1px solid rgba(165, 164, 164, 0.4)',
                borderRadius: '6px',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.8), 0 0 20px rgba(165, 164, 164, 0.1)',
                position: 'relative',
                overflow: 'hidden',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '50%',
                  background: 'linear-gradient(to bottom, rgba(255,255,255,0.05), transparent)',
                  pointerEvents: 'none',
                }
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5" sx={{ fontFamily: "'Playfair Display', serif", color: '#fff', fontSize: '1.5rem' }}>
                    Send a Message
                  </Typography>
                </Box>
              </Box>
              
              <Grid container spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Your Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ color: '#a5a4a4' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#fff',
                        borderRadius: '4px',
                        '& fieldset': { borderColor: 'rgba(165, 164, 164, 0.3)' },
                        '&:hover fieldset': { borderColor: '#a5a4a4' },
                        '&.Mui-focused fieldset': { borderColor: '#ffffff' },
                      },
                      '& .MuiInputLabel-root': { color: '#a1a1a1' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#ffffff' },
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: '#a5a4a4' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#fff',
                        borderRadius: '4px',
                        '& fieldset': { borderColor: 'rgba(165, 164, 164, 0.3)' },
                        '&:hover fieldset': { borderColor: '#a5a4a4' },
                        '&.Mui-focused fieldset': { borderColor: '#ffffff' },
                      },
                      '& .MuiInputLabel-root': { color: '#a1a1a1' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#ffffff' },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Your Message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    multiline
                    rows={6}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 2 }}>
                          <MessageIcon sx={{ color: '#a5a4a4' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#fff',
                        borderRadius: '4px',
                        '& fieldset': { borderColor: 'rgba(165, 164, 164, 0.3)' },
                        '&:hover fieldset': { borderColor: '#a5a4a4' },
                        '&.Mui-focused fieldset': { borderColor: '#ffffff' },
                      },
                      '& .MuiInputLabel-root': { color: '#a1a1a1' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#ffffff' },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    endIcon={<SendIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, #d4af37 0%, #b8962f 100%)',
                      color: '#000000',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      letterSpacing: '2px',
                      textTransform: 'uppercase',
                      padding: '18px',
                      borderRadius: '4px',
                      boxShadow: '0 10px 30px rgba(212, 175, 55, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #e8c84a 0%, #d4af37 100%)',
                        boxShadow: '0 15px 40px rgba(212, 175, 55, 0.5)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.4s ease',
                    }}
                  >
                    Send Message
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default Contact;